<template>
  <div ref="colorPicker" class="t-ModalSelectColor">
    <div
      :style="`background: linear-gradient(90deg, rgba(79, 70, 229, 0) 0%, ${this.selectedColor} 100%), linear-gradient(0deg, #000000 0%, rgba(196, 196, 196, 0) 100%)`"
      class="t-ModalSelectColor-colorSpace"
      ref="colorSpace"
      @mousedown="startMovingThumbWithMouse"
    >
      <div ref="thumb" class="t-ModalSelectColor-picker" tabindex="0"></div>
    </div>
    <div class="t-ModalSelectColor-div">
      <input
        class="t-ModalSelectColor-input"
        type="range"
        step="1"
        min="0"
        max="360"
        :value="colors.hsv.h * 360"
        @keydown.passive="changeInputValue"
        @input="updateHue"
      />
    </div>
    <div class="t-ModalSelectColor-text">
      <span class="t-ModalSelectColor-span" @click="resetColor">{{
        resetLabel
      }}</span>
    </div>
  </div>
</template>

<script>
import { clamp } from './utilities/clamp.js';
import { copyColorObject } from './utilities/copy-color-object.js';
import { colorsAreValueEqual } from './utilities/colors-are-value-equal.js';
import { conversions } from './utilities/convert-color.js';
import { formatAsCssColor } from './utilities/format-as-css-color.js';

const RANGE_INPUT_STEP_FACTOR = 10;
export default {
  props: {
    value: String,
    defaultColor: String,
    resetLabel: {
      type: String,
      default: 'Redefinir',
    },
  },
  data() {
    return {
      selectedColor: '#cf2929',
      isMoving: false,
      colorSpace: null,
      colorPicker: null,
      thumb: null,
      activeFormat: 'hex',
      colors: {
        hex: '#ffffffff',
        hsl: {
          h: 0, s: 0, l: 1, a: 1,
        },
        hsv: {
          h: 0, s: 0, v: 1, a: 1,
        },
        hwb: {
          h: 0, w: 1, b: 0, a: 1,
        },
        rgb: {
          r: 1, g: 1, b: 1, a: 1,
        },
      },
    };
  },
  methods: {
    startMovingThumbWithMouse(event) {
      this.isMoving = true;
      this.moveThumbWithMouse(event);
    },
    moveThumbWithMouse(event) {
      if (
        event.buttons !== 1
        || this.isMoving === false
        || !(this.$refs.colorSpace instanceof HTMLElement)
      ) {
        return;
      }
      this.moveThumb(this.$refs.colorSpace, event.clientX, event.clientY);
    },
    moveThumb(colorSpace, clientX, clientY) {
      const newThumbPosition = this.getNewThumbPosition(
        colorSpace,
        clientX,
        clientY,
      );
      const hsvColor = copyColorObject(this.colors.hsv);
      hsvColor.s = newThumbPosition.x;
      hsvColor.v = newThumbPosition.y;
      this.setColor('hsv', hsvColor);
    },
    getNewThumbPosition(colorSpace, clientX, clientY) {
      const rect = colorSpace.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      return {
        x: clamp(x / rect.width, 0, 1),
        y: clamp(1 - y / rect.height, 0, 1),
      };
    },
    setColor(format, color) {
      const normalizedColor = color;

      if (!colorsAreValueEqual(this.colors[format], normalizedColor)) {
        this.colors[format] = normalizedColor;
        const eventData = this.applyColorUpdates(format);
        this.$emit('setColor', eventData.colors.hex);
      }
    },
    applyColorUpdates(sourceFormat) {
      for (const [format, convert] of conversions[sourceFormat]) {
        this.colors[format] = convert(this.colors[sourceFormat]);
      }

      if (
        this.$refs.colorPicker instanceof HTMLElement
        && this.$refs.colorSpace instanceof HTMLElement
        && this.$refs.thumb instanceof HTMLElement
      ) {
        this.setCssProps(
          this.$refs.colorPicker,
          this.$refs.colorSpace,
          this.$refs.thumb,
          this.colors,
        );
      }

      return this.getEventData(this.colors, this.activeFormat);
    },
    setCssProps(colorPicker, colorSpace, thumb, colors) {
      colorPicker.style.setProperty('--vacp-hsl-h', String(colors.hsl.h));
      colorPicker.style.setProperty('--vacp-hsl-s', String(colors.hsl.s));
      colorPicker.style.setProperty('--vacp-hsl-l', String(colors.hsl.l));
      colorPicker.style.setProperty('--vacp-hsl-a', String(colors.hsl.a));

      colorSpace.setAttribute(
        'style',
        `
        position: relative;
        background-color: hsl(calc(var(--vacp-hsl-h) * 360) 100% 50%); /* 1. */
        background-image:
          linear-gradient(to top, #000, transparent),  /* 2. */
          linear-gradient(to right, #fff, transparent) /* 2. */
        ;
      `,
      );

      thumb.setAttribute(
        'style',
        `
        box-sizing: border-box;
        position: absolute;
        left: ${colors.hsv.s * 100}%;   /* 3. */
        bottom: ${colors.hsv.v * 100}%; /* 3. */
      `,
      );
    },
    getEventData(colors, activeFormat) {
      return {
        colors,
        cssColor: formatAsCssColor(colors[activeFormat], activeFormat),
      };
    },
    changeInputValue(event) {
      if (
        !['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(
          event.key,
        )
        || !event.shiftKey
      ) {
        return;
      }
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);
      const step = parseFloat(input.step);
      const direction = ['ArrowLeft', 'ArrowDown'].includes(event.key) ? -1 : 1;
      const value = parseFloat(input.value) + direction * step * RANGE_INPUT_STEP_FACTOR;
      const newValue = clamp(value, parseInt(input.min), parseInt(input.max));
      input.value = String(newValue - direction * step);
    },
    updateHue(event) {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);
      const hsvColor = copyColorObject(this.colors.hsv);
      hsvColor.h = parseInt(input.value) / 360;

      this.setColor('hsv', hsvColor);
    },
    resetColor() {
      this.$emit('setColor', this.defaultColor);
    },
  },
  mounted() {
    document.addEventListener('mousemove', this.moveThumbWithMouse, {
      passive: false,
    });
  },
};
</script>
<style scoped lang="scss">
.t-ModalSelectColor {
  width: 264px;
  height: 320px;
  background-color: var(--track-color-white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: var(--track-border-radius-sm);
  box-shadow: var(--track-shadow-sm);
  margin-top: var(--track-spacing-bg);
}

.t-ModalSelectColor-colorSpace {
  width: 232px;
  height: 232px;
  overflow: hidden;
  border-radius: var(--track-border-radius-sm);
}

.t-ModalSelectColor-input {
  -webkit-appearance: none;
  width: 232px;
  height: 8px;
  border-radius: 100px;
  background: linear-gradient(
    90deg,
    #ff0000 5.21%,
    #ff8a00 16.48%,
    #ffe600 27.74%,
    #14ff00 39.35%,
    #00a3ff 49.37%,
    #0500ff 61.18%,
    #ad00ff 72.26%,
    #ff00c7 83.53%,
    #ff0000 94.61%
  );
}

.t-ModalSelectColor-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 8px;
  height: 8px;
  border: 2px solid var(--track-color-white);
  border-radius: 100px;
  filter: drop-shadow(0px 2px 4px rgba(31, 41, 55, 0.06))
    drop-shadow(0px 4px 6px rgba(31, 41, 55, 0.1));
}

.t-ModalSelectColor-text {
  width: 232px;
  position: static;
  font-family: var(--track-font-family-base);
  font-weight: var(--track-font-weight-semibold);
  font-size: var(--track-font-size-xs);
  line-height: 16px;
  color: var(--track-color-brand-primary);
  &:hover {
    color: var(--track-color-brand-primary-darkest);
  }
}

.t-ModalSelectColor-span {
  cursor: pointer;
}

.t-ModalSelectColor-picker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 2px 2px 6px 0 rgba(0, 0, 0, 0.3);
}

.t-ModalSelectColor-div {
  display: flex;
  align-items: center;
  margin: var(--track-spacing-md) 0px;
}
</style>
